import { SearchIcon } from 'lucide-react';
import { getModifierKeyLabel } from '../lib/platform';
import { cn } from '../lib/utils';
import { useSearchState } from '../stores/ui-store';
import { Button } from './ui/button';

/**
 * Butonul care deschide paleta de comenzi. Arată ca un câmp de căutare, nu e unul.
 *
 * **Lăţimea vine de la container, nu de la viewport.** Varianta din shadcn-admin avea
 * `sm:w-40 lg:w-52 xl:w-64`, pentru că acolo stă într-un antet lat. Aici stă în sidebar,
 * care are 16rem: pe ecran mare, `xl:w-64` însemna exact lăţimea sidebar-ului, iar cu
 * `px-3` de o parte şi de alta butonul ieşea afară şi tăia badge-ul `⌘K` (tichetul #48).
 *
 * Cine îl pune într-un antet lat îi dă lăţimea prin `className` — un `w-*` din afară bate
 * `w-full` de aici, prin `tailwind-merge`.
 *
 * **Modificatorul se citeşte din platformă** (#76): `⌘` doar pe Apple, `Ctrl` pe
 * Windows/Linux. Eticheta mai lungă cere şi spaţiu rezervat mai mare la capăt — altfel
 * badge-ul intră peste text, exact defectul reparat în #48 pentru cealaltă cauză.
 */
export function Search({
  className = '',
  placeholder = 'Search',
  ...props
}: React.ComponentProps<'button'> & { placeholder?: string }) {
  const { setOpen } = useSearchState();
  const modifier = getModifierKeyLabel();
  const isSymbol = modifier === '⌘';
  return (
    <Button
      {...props}
      variant="outline"
      className={cn(
        'group relative h-8 w-full justify-start rounded-md bg-muted/25 text-sm font-normal text-muted-foreground shadow-none hover:bg-accent',
        isSymbol ? 'pe-12' : 'pe-16',
        className,
      )}
      aria-keyshortcuts="Meta+K Control+K"
      onClick={() => setOpen(true)}
    >
      <SearchIcon
        aria-hidden="true"
        className="absolute inset-s-1.5 top-1/2 -translate-y-1/2"
        size={16}
      />
      <span className="ms-4 truncate">{placeholder}</span>
      {/* Vizibil şi pe mobil: în sidebar încape oricând, iar `hidden sm:flex` îl făcea să
          apară şi să dispară cu lăţimea ferestrei, nu cu spaţiul disponibil. */}
      <kbd className="pointer-events-none absolute inset-e-[0.3rem] top-[0.3rem] flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 select-none group-hover:bg-accent">
        <span className={isSymbol ? 'text-xs' : undefined}>{modifier}</span>K
      </kbd>
    </Button>
  );
}
